import { useTranslation } from "react-i18next";
import {
  GridColDef,
  getGridStringOperators,
  GridRowParams,
  GridFilterInputValue
} from "@mui/x-data-grid";
import type {
  GridFilterItem,
  GridCellParams,
  GridFilterOperator
} from "@mui/x-data-grid-premium";
import { Role } from "../Auth/roles";
import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useAppSelector } from "../../app/hooks";
import { selectUserRoles } from "./usersSlice";
import { SysAdminsUsersTable } from "./SysAdminsUsersTable";
import { ProgramAdminsUsersTable } from "./ProgramAdminsUsersTable";
import { EditUser } from "./EditUser";
import { useDispatch } from "react-redux";
import { setModalContent, setShowModal } from "../../common-ui/modalSlice";
import { ProgramMembership } from "../../API";
import { selectPrograms } from "../Programs/programsSlice";
import { useEffect, useState, useMemo, useCallback } from "react";
import { Mixpanel } from "../Analytics/Mixpanel";
import { MixpanelEvents } from "../../app/constants";
import { useTrainingStages } from "../TrainingStages/trainingStagesAPI";
import { formatPgyAndStage, clinicalYearSortKey } from "../../app/utils";

const pageDetails: PageWrapperProps = {
  className: "Users",
  headerText: "Users"
};

type UsersProps = {
  disableVirtualization?: boolean;
};

export const Users = ({ disableVirtualization = false }: UsersProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedProgramId, setSelectedProgramId] = useState<string>("");
  const allPrograms = useAppSelector(selectPrograms);
  const { trainingStages } = useTrainingStages();
  const currentUsersRoles = useAppSelector(selectUserRoles);
  const isSysAdmin = currentUsersRoles?.includes(Role.SystemAdmin);
  const isProgramAdmin = currentUsersRoles?.includes(Role.ProgramAdmin);

  // Shared extraction for the "Clinical Year" column: resolve the row's active
  // Trainee membership and its training stage. Returns null for non-trainees.
  const getClinicalYearParts = (row: any) => {
    const programMemberships: ProgramMembership[] =
      row?.programMemberships?.items ?? [];
    const activeTraineeMembership = programMemberships.find(
      (pm) => !pm.endDate && pm.programRole === "Trainee"
    );
    if (!activeTraineeMembership) return null;

    const pgy: number | null | undefined = row.pgy;
    const yearInStage =
      (activeTraineeMembership as any)?.yearInStage ?? null;
    const trainingStageId =
      (activeTraineeMembership?.program as any)?.trainingStageId ??
      (allPrograms.find(
        (p) => p.id === activeTraineeMembership?.programId
      ) as any)?.trainingStageId ??
      null;
    const stage = trainingStageId
      ? trainingStages.find((s) => s.id === trainingStageId)
      : undefined;
    return { pgy, yearInStage, stage };
  };

  // Filter the "Clinical Year" column by the actual year shown (not the numeric
  // sort-key value), and never match non-trainees.
  const clinicalYearFilterOperators: GridFilterOperator[] = [
    {
      label: "equals",
      value: "equals",
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (filterItem.value == null || filterItem.value === "") return null;
        const target = parseInt(String(filterItem.value), 10);
        if (Number.isNaN(target)) return null;
        return (params: GridCellParams) => {
          const parts = getClinicalYearParts(params.row);
          if (!parts) return false;
          const py = parts.pgy == null ? null : Number(parts.pgy);
          const yr = parts.yearInStage == null ? null : Number(parts.yearInStage);
          if (parts.stage?.code === "UME") return yr === target;
          if (parts.stage?.code === "FELLOWSHIP")
            return py === target || yr === target;
          return (py ?? yr) === target;
        };
      },
      InputComponent: GridFilterInputValue,
      InputComponentProps: { type: "number" }
    }
  ];

  const preColumns: GridColDef[] = [
    {
      field: "firstName",
      headerName: t("First Name")
    },
    {
      field: "lastName",
      headerName: t("Last Name")
    },
    {
      field: "email",
      headerName: t("Email")
    },
    {
      field: "role",
      headerName: t("Role"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items ?? [];
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const roleSet = new Set<string>();

        activeMemberships?.forEach((membership) => {
          const role = membership.programRole;
          if (role === "Trainee" || role === "Attending") {
            roleSet.add("Clinician");
          } else {
            roleSet.add(role);
          }
        });

        return Array.from(roleSet).join(", ");
      }
    },
    {
      field: "jobTitle",
      headerName: t("Job Title"),
      valueGetter: (params) => {
        const jobTitle = params.row.jobTitle;
        if (jobTitle) return jobTitle;

        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items || [];

        const activeMemberships = programMemberships.filter((m) => !m.endDate);

        // Define priority order
        const priority = ["Trainee", "Attending", "ProgramAdmin"];

        // Find highest priority role
        for (const role of priority) {
          if (activeMemberships.find((m) => m.programRole === role)) {
            return role;
          }
        }

        // If none match priority, return first available role or "N/A"
        return activeMemberships[0]?.programRole || "N/A";
      }
    },
    {
      field: "pgy",
      headerName: "Clinical Year",
      // Value is the numeric sort key (so sort is by meaning, not label text);
      // renderCell draws the human label.
      valueGetter: (params) =>
        clinicalYearSortKey(getClinicalYearParts(params.row)),
      // When grouped, bucket + label the groups by the human clinical-year label
      // (e.g. "PGY-1", "MS-2") so the group header shows "PGY-1 (5)" instead of "(5)".
      groupingValueGetter: (params: any) => {
        const parts = getClinicalYearParts(params.row);
        if (!parts) return "";
        return (
          formatPgyAndStage(
            parts.pgy,
            parts.stage?.yearLabelPrefix,
            parts.yearInStage,
            parts.stage?.code
          ) || ""
        );
      },
      sortComparator: (v1, v2) => (v1 as number) - (v2 as number),
      filterOperators: clinicalYearFilterOperators,
      renderCell: (params) => {
        // Group-header row (when grouped by Clinical Year): show the group's
        // label, which groupingValueGetter stored as the node's groupingKey.
        const rowNode: any = params.rowNode;
        if (rowNode?.type === "group") {
          return rowNode.groupingKey || "";
        }
        const parts = getClinicalYearParts(params.row);
        if (!parts) return ""; // non-trainees have no clinical year
        return (
          formatPgyAndStage(
            parts.pgy,
            parts.stage?.yearLabelPrefix,
            parts.yearInStage,
            parts.stage?.code
          ) || "-"
        );
      }
    },
    {
      field: "program",
      headerName: t("Program(s)"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items ?? [];
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) => programMembership.programId === program.id
          )
        );
        const names = programsList?.map((program) => program.name).filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    },
    {
      field: "trainingProgram",
      headerName: t("Training Program"),
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items ?? [];
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) =>
              programMembership.programId === program.id &&
              programMembership.programRole === "Trainee"
          )
        );
        const names = programsList?.map((program) => program.name).filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    },
    {
      field: "status",
      headerName: t("Status")
    },
    {
      field: "npi",
      headerName: "NPI"
    },
    {
      field: "institutionName",
      headerName: t("Institution(s)"),
      // We currently have no way to sort Users by Institution on the backend.
      sortable: false,
      valueGetter: (params) => {
        const programMemberships: ProgramMembership[] =
          params.row.programMemberships?.items ?? [];
        const activeMemberships = programMemberships?.filter(
          (programMembership) => !programMembership.endDate
        );
        const programsList = allPrograms.filter((program) =>
          activeMemberships?.some(
            (programMembership) => programMembership.programId === program.id
          )
        );
        const names = programsList
          ?.map((program) => program.institution?.name)
          .filter(Boolean) as string[];
        return Array.from(new Set(names)).join(", ");
      }
    }
  ];

  // Memoize columns to prevent recreating on every render, which could cause remounts
  const columns = useMemo(() => {
    return preColumns.map((column) => {
      const isFilterableField = ["role", "trainingProgram", "jobTitle"].includes(
        column.field
      );

      return {
        ...column,
        flex: column.flex ?? 1,
        filterOperators: isFilterableField
          ? [
              {
                ...(getGridStringOperators().find(
                  (op) => op.value === "contains"
                ) ?? {
                  label: "Contains",
                  value: "contains",
                  getApplyFilterFn: () => null
                }),
                getApplyFilterFn: (filterItem: GridFilterItem) => {
                  if (!filterItem.value) return null;
                  return (params: GridCellParams) => {
                    if (!filterItem.value) return false; // Value is required to apply the filter
                    const filterValue = filterItem.value.toString().toLowerCase();
                    const cellValue = (params.value ?? "")
                      .toString()
                      .toLowerCase();
                    return cellValue.includes(filterValue);
                  };
                }
              }
            ]
          : getGridStringOperators().filter(
              (operator) => operator.value === "contains"
            )
      };
    });
  }, [t, allPrograms, trainingStages]);

  // Memoize handleRowClick to prevent table remounts
  const handleRowClick = useCallback((rowData: GridRowParams<any>) => {
    const { row } = rowData;
    const { id } = row;
    const content = <EditUser id={id} />;
    dispatch(setModalContent(content));
    dispatch(setShowModal(true));
  }, [dispatch]);

  // Memoize table components to prevent remounts
  const sysAdminTable = useMemo(
    () => (
      <SysAdminsUsersTable
        columns={columns}
        disableVirtualization={disableVirtualization}
        handleRowClick={handleRowClick}
        selectedProgramId={selectedProgramId}
        setSelectedProgramId={setSelectedProgramId}
      />
    ),
    [columns, disableVirtualization, handleRowClick, selectedProgramId, setSelectedProgramId]
  );
  
  const programAdminTable = useMemo(
    () => (
      <ProgramAdminsUsersTable
        columns={columns}
        disableVirtualization={disableVirtualization}
        handleRowClick={handleRowClick}
      />
    ),
    [columns, disableVirtualization, handleRowClick]
  );

  const tableToDisplay = useMemo(() => {
    return isSysAdmin
      ? sysAdminTable
      : isProgramAdmin
      ? programAdminTable
      : null;
  }, [isSysAdmin, isProgramAdmin, sysAdminTable, programAdminTable]);

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.HomePageViewed);
  }, []);

  return <PageWrapper {...pageDetails}>{tableToDisplay}</PageWrapper>;
};
